import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Button, Table, Spinner } from 'react-bootstrap';
import { IAddress } from '../index';
import styles from './AddressesTab.module.css';
import api from '../../../services/api';
import { notify } from '../../../stores/useNotificationStore';
import SearchSelectField, { SearchOption } from '../../../components/SearchSelectField';
import { FiSave, FiPlus, FiX, FiEdit2, FiTrash2 } from '../../../utils/icons';
import { useT } from '../../../i18n';

interface AddressesTabProps {
  addresses: IAddress[];
  setAddresses: React.Dispatch<React.SetStateAction<IAddress[]>>;
  empresaId: number | null;
}

interface CadastroAuxiliarItem { id: number; nome: string; ativo: boolean; }

const initialAddressState = {
  id: 0,
  tipo_endereco: '',
  cep: '',
  endereco: '',
  numero: '',
  bairro: '',
  cidade: '',
  estado: '',
};

const formatCep = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

const AddressesTab: React.FC<AddressesTabProps> = ({ addresses, setAddresses, empresaId }) => {
  const t = useT();
  const [newAddress, setNewAddress] = useState<Omit<IAddress, 'id'>>(initialAddressState);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [tiposEndereco, setTiposEndereco] = useState<CadastroAuxiliarItem[]>([]);
  const [buscandoCep, setBuscandoCep] = useState(false);

  useEffect(() => {
    api.getCadastrosAuxiliares('tipo_endereco', empresaId ?? undefined).then(res => {
      if (res.success && Array.isArray(res.data)) setTiposEndereco(res.data as CadastroAuxiliarItem[]);
    });
  }, [empresaId]);

  const tipoOptions: SearchOption[] = tiposEndereco.filter(t => t.ativo !== false).map(t => ({ key: t.nome, label: t.nome }));

  const criarTipoEndereco = async (nome: string): Promise<SearchOption | null> => {
    if (!empresaId) { notify.warning(t('companyRegistration.addresses.selectCompanyFirst')); return null; }
    try {
      const res = await api.createCadastroAuxiliar({ tipo: 'tipo_endereco', nome, empresa_id: empresaId });
      if (res.success && res.data) {
        setTiposEndereco(prev => [...prev, res.data as CadastroAuxiliarItem]);
        notify.success(t('companyRegistration.addresses.createdSuccess'));
        return { key: nome, label: nome };
      }
      notify.error((res.error as string) || t('companyRegistration.addresses.createError'));
      return null;
    } catch {
      notify.error(t('companyRegistration.addresses.connectionError'));
      return null;
    }
  };

  // Ao completar os 8 dígitos do CEP, preenche Endereço/Bairro/Cidade/Estado (ViaCEP).
  const buscarCep = async (cepFormatado: string) => {
    const digits = cepFormatado.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const json = await resp.json();
      if (json && !json.erro) {
        setNewAddress(prev => ({
          ...prev,
          endereco: json.logradouro || prev.endereco,
          bairro: json.bairro || prev.bairro,
          cidade: json.localidade || prev.cidade,
          estado: json.uf || prev.estado,
        }));
      } else {
        notify.warning(t('companyRegistration.addresses.cepNotFound'));
      }
    } catch {
      notify.warning(t('companyRegistration.addresses.cepError'));
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'cep') {
      const formatted = formatCep(value);
      setNewAddress(prev => ({ ...prev, cep: formatted }));
      if (formatted.replace(/\D/g, '').length === 8) buscarCep(formatted);
    } else {
      setNewAddress(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddAddress = () => {
    if (newAddress.cep && newAddress.endereco) {
      if (editingId !== null) {
        setAddresses(prev => prev.map(a => a.id === editingId ? { ...newAddress, id: editingId } : a));
        setEditingId(null);
      } else {
        setAddresses(prev => [...prev, { ...newAddress, id: Date.now() }]);
      }
      setNewAddress(initialAddressState);
    }
  };

  const handleEditAddress = (address: IAddress) => {
    const { id, ...rest } = address;
    setNewAddress(rest);
    setEditingId(id);
  };

  const handleCancelEdit = () => {
    setNewAddress(initialAddressState);
    setEditingId(null);
  };

  const handleRemoveAddress = (id: number) => {
    setAddresses(prev => prev.filter(address => address.id !== id));
    if (editingId === id) {
      setNewAddress(initialAddressState);
      setEditingId(null);
    }
  };

  return (
    <div>
      <Form as={Row} className={`g-3 ${styles.formRow}`}>
        <Col md={4}>
          <Form.Group controlId="addressTipo">
            <Form.Label>{t('companyRegistration.addresses.tipoEndereco')}</Form.Label>
            <SearchSelectField
              placeholder={t('companyRegistration.addresses.tipoEnderecoPlaceholder')}
              options={tipoOptions}
              value={newAddress.tipo_endereco}
              onSelect={opt => setNewAddress(prev => ({ ...prev, tipo_endereco: opt?.key || '' }))}
              onAdd={criarTipoEndereco}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="addressCep">
            <Form.Label>{t('companyRegistration.addresses.cep')}</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control type="text" name="cep" value={newAddress.cep} onChange={handleInputChange} placeholder="00000-000" />
              {buscandoCep && <Spinner animation="border" size="sm" />}
            </div>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="addressEndereco">
            <Form.Label>{t('companyRegistration.addresses.endereco')}</Form.Label>
            <Form.Control type="text" name="endereco" value={newAddress.endereco} onChange={handleInputChange} placeholder={t('companyRegistration.addresses.enderecoPlaceholder')} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="addressNumero">
            <Form.Label>{t('companyRegistration.addresses.numero')}</Form.Label>
            <Form.Control type="text" name="numero" value={newAddress.numero} onChange={handleInputChange} />
          </Form.Group>
        </Col>
        <Col md={8}>
          <Form.Group controlId="addressBairro">
            <Form.Label>{t('companyRegistration.addresses.bairro')}</Form.Label>
            <Form.Control type="text" name="bairro" value={newAddress.bairro} onChange={handleInputChange} placeholder={t('companyRegistration.addresses.enderecoPlaceholder')} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="addressCidade">
            <Form.Label>{t('companyRegistration.addresses.cidade')}</Form.Label>
            <Form.Control type="text" name="cidade" value={newAddress.cidade} onChange={handleInputChange} placeholder={t('companyRegistration.addresses.enderecoPlaceholder')} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="addressEstado">
            <Form.Label>{t('companyRegistration.addresses.estado')}</Form.Label>
            <Form.Control type="text" name="estado" value={newAddress.estado} onChange={handleInputChange} placeholder={t('companyRegistration.addresses.enderecoPlaceholder')} />
          </Form.Group>
        </Col>
        <Col md={12} className="d-flex gap-2">
          <Button variant="primary" onClick={handleAddAddress}>
            {editingId !== null ? <><FiSave /> {t('companyRegistration.addresses.saveChanges')}</> : <><FiPlus /> {t('companyRegistration.addresses.addAddress')}</>}
          </Button>
          {editingId !== null && (
            <Button variant="secondary" onClick={handleCancelEdit}><FiX /> {t('companyRegistration.addresses.cancel')}</Button>
          )}
        </Col>
      </Form>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>{t('companyRegistration.addresses.tipoEndereco')}</th>
            <th>{t('companyRegistration.addresses.cep')}</th>
            <th>{t('companyRegistration.addresses.endereco')}</th>
            <th>{t('companyRegistration.addresses.numero')}</th>
            <th>{t('companyRegistration.addresses.bairro')}</th>
            <th>{t('companyRegistration.addresses.cidade')}</th>
            <th>{t('companyRegistration.addresses.estado')}</th>
            <th>{t('companyRegistration.addresses.tableActions')}</th>
          </tr>
        </thead>
        <tbody>
          {addresses.map(address => (
            <tr key={address.id}>
              <td>{address.tipo_endereco}</td>
              <td>{address.cep}</td>
              <td>{address.endereco}</td>
              <td>{address.numero}</td>
              <td>{address.bairro}</td>
              <td>{address.cidade}</td>
              <td>{address.estado}</td>
              <td className="d-flex gap-1">
                <Button variant="outline-primary" size="sm" onClick={() => handleEditAddress(address)}><FiEdit2 /> {t('companyRegistration.addresses.edit')}</Button>
                <Button variant="outline-danger" size="sm" onClick={() => handleRemoveAddress(address.id)}><FiTrash2 /> {t('companyRegistration.addresses.remove')}</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default AddressesTab;
